import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { colors, shadows } from '@theme/colors';
import {
  Home,
  Scale,
  Calendar,
  AlertTriangle,
  Mountain,
  TrendingUp,
  FileText,
  BookOpen,
  DollarSign,
  Shovel,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { path: '/', label: 'Portal', icon: <Home size={18} /> },
  { path: '/reequilibrio', label: 'Reequilíbrio', icon: <Scale size={18} /> },
  { path: '/cronograma', label: 'Cronograma', icon: <Calendar size={18} /> },
  {
    path: '/disrupcao',
    label: 'Disrupção',
    icon: <AlertTriangle size={18} />,
    children: [
      { path: '/disrupcao', label: 'Dashboard', icon: <AlertTriangle size={16} /> },
      { path: '/disrupcao/trimestral', label: 'Trimestral', icon: <Calendar size={16} /> },
      { path: '/disrupcao/tipping', label: 'Tipping Point', icon: <TrendingUp size={16} /> },
    ],
  },
  {
    path: '/geologia',
    label: 'Geologia',
    icon: <Mountain size={18} />,
    children: [
      { path: '/geologia', label: 'RQD', icon: <Mountain size={16} /> },
      { path: '/geologia/perfuracao', label: 'Perfuração', icon: <Shovel size={16} /> },
      { path: '/geologia/perfil', label: 'Perfil', icon: <TrendingUp size={16} /> },
    ],
  },
  { path: '/produtividade', label: 'Produtividade', icon: <TrendingUp size={18} /> },
  { path: '/atas', label: 'Atas', icon: <FileText size={18} /> },
  { path: '/fundacoes', label: 'Fundações', icon: <Shovel size={18} /> },
  {
    path: '/relatorios',
    label: 'Relatórios',
    icon: <BookOpen size={18} />,
    children: [
      { path: '/relatorios/v4', label: 'Versão 4', icon: <BookOpen size={16} /> },
      { path: '/relatorios/v5', label: 'Versão 5', icon: <BookOpen size={16} /> },
    ],
  },
  {
    path: '/financeiro',
    label: 'Financeiro',
    icon: <DollarSign size={18} />,
    children: [
      { path: '/financeiro', label: 'R$ 130M', icon: <DollarSign size={16} /> },
      { path: '/terraplenagem', label: 'Terraplenagem', icon: <Shovel size={16} /> },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  const toggleExpand = (path: string) => {
    setExpandedItems((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item: NavItem, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.path);
    const active = isActive(item.path);

    if (hasChildren) {
      return (
        <div key={item.path}>
          <button
            onClick={() => toggleExpand(item.path)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              padding: '12px 16px',
              border: 'none',
              background: active ? 'rgba(224, 123, 61, 0.1)' : 'transparent',
              color: active ? colors.brand.laranjaManta : colors.neutral.gray700,
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: active ? 600 : 400,
              textAlign: 'left',
              borderRadius: 8,
              transition: 'all 0.2s ease',
            }}
          >
            {item.icon}
            <span style={{ flex: 1 }}>{item.label}</span>
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          {isExpanded && (
            <div style={{ paddingLeft: 20 }}>
              {item.children!.map((child) => renderNavItem(child, true))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={onClose}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: isChild ? '10px 16px' : '12px 16px',
          textDecoration: 'none',
          color: active ? colors.brand.laranjaManta : colors.neutral.gray700,
          background: active ? 'rgba(224, 123, 61, 0.1)' : 'transparent',
          borderRadius: 8,
          fontSize: isChild ? '0.85rem' : '0.9rem',
          fontWeight: active ? 600 : 400,
          transition: 'all 0.2s ease',
        }}
      >
        {item.icon}
        {item.label}
      </NavLink>
    );
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 998,
            display: 'none',
          }}
        />
      )}
      <aside
        style={{
          width: isOpen ? 260 : 0,
          minHeight: 'calc(100vh - 60px)',
          background: colors.neutral.white,
          borderRight: `1px solid ${colors.neutral.gray200}`,
          transition: 'width 0.3s ease',
          overflow: 'hidden',
          position: 'sticky',
          top: 60,
          flexShrink: 0,
        }}
      >
        <nav style={{ padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {navItems.map((item) => renderNavItem(item))}
        </nav>
      </aside>
    </>
  );
}

export default Sidebar;
